import { useEffect, useMemo } from 'react';
import { Logo } from '@/components/logos/logo';
import {
  DialogHeader,
  DialogFooter,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Form, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  IntegrationTypeV2,
  IntegrationsV2Query,
  CreateIntegrationV2Mutation,
  CreateIntegrationV2MutationVariables,
  CurrentMerchantForCompanyInformationQuery,
  IntegrationV2Properties,
  Merchant
} from '@/gql/graphql';
import { gql } from '@/gql/gql';
import { INTEGRATIONS_V2_QUERY } from '@/queries/integration';
import { useQuery, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { AppTitle, AppTitleText } from '@/components/app-header/app-header';
import { IntegrationDiagram } from './components/integration-diagram';
import { TrackingCardsComponent } from './components/tracking-cards-component';
import { TrackingDiagramPageLoading } from './components/loading-component';
import { CURRENT_MERCHANT_QUERY } from '../settings/merchant/company-information-form';
import { TrackingDiagramIntegrationsErrorMessage } from './components/tracking-diagram-integrations-error-message';
import { useFeature } from '@/hooks/use-feature';
export function TrackingDiagramPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useQuery<IntegrationsV2Query>(INTEGRATIONS_V2_QUERY);
  const { data: merchantInfoData, loading: merchantInfoLoading } =
    useQuery<CurrentMerchantForCompanyInformationQuery>(CURRENT_MERCHANT_QUERY);
  const destinations = useMemo(() => data?.integrationsV2, [data]);
  const merchantInfo = useMemo(() => merchantInfoData?.currentMerchant, [merchantInfoData]);
  return (
    <div className="flex flex-col h-screen w-full">
      <AppTitle>
        <AppTitleText>Tracking</AppTitleText>
      </AppTitle>
      {loading || merchantInfoLoading ? (
        <TrackingDiagramPageLoading />
      ) : (
        <>
          <IntegrationDiagram
            merchantInfo={merchantInfo as Merchant | undefined}
            destinations={destinations as IntegrationV2Properties[] | undefined}
          />
          <TrackingDiagramIntegrationsErrorMessage
            destinations={destinations as IntegrationV2Properties[] | undefined}
          />
          <TrackingCardsComponent
            destinations={destinations as IntegrationV2Properties[] | undefined}
          />
          <CreateDialog
            open={searchParams.has('new')}
            onClose={() => {
              setSearchParams({});
            }}
          />
        </>
      )}
    </div>
  );
}

const formSchema = z.object({
  type: z.enum(IntegrationTypeV2),
  name: z
    .string()
    .min(1, 'Integration name is required')
    .max(30, 'Integration name cannot exceed 30 characters')
});

const CREATE_INTEGRATION_MUTATION = gql(/* GraphQL */ `
  mutation CreateIntegrationV2($input: CreateIntegrationInputV2!) {
    createIntegrationV2(input: $input) {
      id
      name
      type
      status
    }
  }
`);

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { data: existingIntegrations } = useQuery<IntegrationsV2Query>(INTEGRATIONS_V2_QUERY);
  const isSendlaneEnabled = useFeature('SENDLANE');
  const [createIntegration] = useMutation<
    CreateIntegrationV2Mutation,
    CreateIntegrationV2MutationVariables
  >(CREATE_INTEGRATION_MUTATION, {
    refetchQueries: [{ query: INTEGRATIONS_V2_QUERY }]
  });

  const getDefaultName = (type: IntegrationTypeV2) => {
    const typeNames = {
      [IntegrationTypeV2.Meta]: 'Meta',
      [IntegrationTypeV2.Ga4]: 'Google Analytics',
      [IntegrationTypeV2.Klaviyo]: 'Klaviyo',
      [IntegrationTypeV2.Postscript]: 'Postscript',
      [IntegrationTypeV2.Snapchat]: 'Snapchat',
      [IntegrationTypeV2.Tiktok]: 'Tiktok',
      [IntegrationTypeV2.Googleads]: 'Google Ads',
      [IntegrationTypeV2.Sendlane]: 'Sendlane',
      [IntegrationTypeV2.Attentive]: 'Attentive',
      [IntegrationTypeV2.Redo]: 'Redo',
      [IntegrationTypeV2.Webhook]: 'Webhook',
      [IntegrationTypeV2.Unspecified]: 'Integration'
    };

    const baseName = typeNames[type];
    const existingCount =
      existingIntegrations?.integrationsV2.filter((integration) => integration.type === type)
        .length ?? 0;

    return existingCount > 0 ? `${baseName} ${existingCount + 1}` : baseName;
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      type: IntegrationTypeV2.Meta,
      name: getDefaultName(IntegrationTypeV2.Meta)
    },
    mode: 'onSubmit'
  });

  // Watch for type changes to update name automatically
  const selectedType = form.watch('type');
  useEffect(() => {
    form.setValue('name', getDefaultName(selectedType));
  }, [selectedType, existingIntegrations?.integrationsV2]);

  const handleSubmit = async (input: z.infer<typeof formSchema>) => {
    const result = await createIntegration({
      variables: {
        input
      }
    });

    if (result.data?.createIntegrationV2) {
      void navigate(`./integrations/${result.data.createIntegrationV2.id}`, {
        state: { isSetup: true }
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new integration</DialogTitle>
          <DialogDescription className="hidden">
            Connect merchant data to third party data platforms
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Form {...form}>
            <form
              id="createIntegrationForm"
              className="space-y-4"
              onSubmit={(e) => {
                void form.handleSubmit(handleSubmit)(e);
              }}
            >
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Integration type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an Integration Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={IntegrationTypeV2.Meta}>
                            <div className="p-2 flex items-center">
                              <Logo icon="meta" /> <span className="ml-2">Meta</span>
                            </div>
                          </SelectItem>
                          {/* <SelectItem value={IntegrationTypeV2.Ga4}>
                            <div className="p-2 flex items-center">
                              <Logo icon="ga4" /> <span className="ml-2">Google Analytics</span>
                              <Badge>Beta</Badge>
                            </div>
                          </SelectItem> */}
                          <SelectItem value={IntegrationTypeV2.Klaviyo}>
                            <div className="p-2 flex items-center">
                              <Logo icon="klaviyo" /> <span className="ml-2">Klaviyo</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={IntegrationTypeV2.Postscript}>
                            <div className="p-2 flex items-center">
                              <Logo icon="postscript" /> <span className="ml-2">Postscript</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={IntegrationTypeV2.Snapchat}>
                            <div className="p-2 flex items-center">
                              <Logo icon="snapchat" /> <span className="ml-2">Snapchat</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={IntegrationTypeV2.Tiktok}>
                            <div className="p-2 flex items-center">
                              <Logo icon="tiktok" /> <span className="ml-2">Tiktok</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={IntegrationTypeV2.Redo}>
                            <div className="p-2 flex items-center">
                              <Logo icon="redo" /> <span className="ml-2">Redo</span>
                            </div>
                          </SelectItem>
                          <SelectItem value={IntegrationTypeV2.Webhook}>
                            <div className="p-2 flex items-center">
                              <Logo icon="webhook" /> <span className="ml-2">Webhook</span>
                            </div>
                          </SelectItem>
                          {isSendlaneEnabled && (
                            <SelectItem value={IntegrationTypeV2.Sendlane}>
                              <div className="p-2 flex items-center">
                                <Logo icon="sendlane" /> <span className="ml-2">Sendlane</span>
                              </div>
                            </SelectItem>
                          )}
                          <SelectItem value={IntegrationTypeV2.Attentive}>
                            <div className="p-2 flex items-center">
                              <Logo icon="attentive" /> <span className="ml-2">Attentive</span>
                            </div>
                          </SelectItem>

                          {/* <SelectItem value={IntegrationTypeV2.Googleads}>
                            <div className="p-2 flex items-center">
                              <Logo icon="googleads" /> <span className="ml-2">Google Ads</span>
                              <Badge>Beta</Badge>
                            </div>
                          </SelectItem> */}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Integration Name</FormLabel>
                    <Input placeholder="Name" {...field} maxLength={30} />
                    {form.formState.errors.name && (
                      <p className="text-xs text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button type="submit" form="createIntegrationForm" disabled={!form.formState.isValid}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
